import { AttachState } from '@fluidframework/container-definitions';
import { ActionDebouncer } from '../../../utilities/ActionDebouncer';

export interface IContainer {
  on(event: 'connected', cb: () => void): void;
  on(event: 'disconnected', cb: () => void): void;

  removeListener: (event: 'connected' | 'disconnected', cb: () => void) => void;

  connected: boolean;

  attachState: AttachState;
}

/**
 * How long we should wait once we are disconnected before notifying the user that they are disconnected.
 * If the user is active when we get disconnected, the wait starts right away.
 * If the user is inactive when we get disconnected, the wait starts when they become active again.
 */
const waitForDisconnectedNotifyInMs = 15000;

/**
 * Friendly API for deriving UX around connected state of the container. It has special logic to make sure
 * we show the right state on boot and that we don't show the disconnected UI too quickly in the event of returning
 * from a long idle period or in cases of intermittent connectivity loss.
 */
export class ContainerConnectionStateTracker {
  private lastEmittedConnectionValue: 'connected' | 'disconnected' | undefined;

  private readonly disconnectDebouncer: ActionDebouncer;

  constructor(
    private container: IContainer,
    private onConnectionStateChangeHandler: (newState: 'connected' | 'disconnected') => void
  ) {
    this.disconnectDebouncer = new ActionDebouncer(
      () => this.emitConnectionStateChange('disconnected'),
      waitForDisconnectedNotifyInMs
    );

    container.on('connected', this.onConnect);
    container.on('disconnected', this.onDisconnect);

    this.scheduleConnectionMessage();
  }

  /**
   * Puts the tracker back into the initial state so that the listener can be brought back into a good state.
   * This is useful for scenarios like bringing the connection state back up to date after an intentional extended offline period.
   */
  public reset() {
    // Act as if the listener has not received any messages
    this.lastEmittedConnectionValue = undefined;

    this.scheduleConnectionMessage();
  }

  /**
   * This API is used to explicitly send a disconnect/connect message to the listener so it can b
   */
  private scheduleConnectionMessage() {
    if (this.container.connected) {
      this.onConnect();
    } else {
      this.onDisconnect();
    }
  }

  /**
   * Emits a connection state change if it is different from the last event we emitted.
   */
  private emitConnectionStateChange = (newState: 'connected' | 'disconnected') => {
    // Container only emits connection signals after attach
    if (this.container.attachState === AttachState.Attached && this.lastEmittedConnectionValue !== newState) {
      this.onConnectionStateChangeHandler(newState);
    }

    this.lastEmittedConnectionValue = newState;
  };

  private onConnect = () => {
    // If we reconnect before the disconnect timer fires, let's clear any pending (debounced) disconnects
    this.disconnectDebouncer.cancelPendingDebounce();

    this.emitConnectionStateChange('connected');
  };

  private onDisconnect = () => {
    // We don't want to emit the disconnect event right away to the user, instead we give the container some time to connect
    this.disconnectDebouncer.startOrRefreshDebounce();
  };

  dispose() {
    this.container.removeListener('connected', this.onConnect);
    this.container.removeListener('disconnected', this.onDisconnect);
    this.disconnectDebouncer.dispose();
  }
}

// cspell:ignore Debouncer
